import React, { PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react'
import debounce from 'lodash.debounce'
import { useIntl } from 'react-intl'
import { IconRegularPin, IconRegularSearch } from '@notino/react-styleguide'

// hooks
import useMessages from '../../hooks/useMessages'
import useIsMobile from '../../hooks/useIsMobile'

// types
import { GetSalonFiltersQueryParams, SalonPageRedirectQueryData, SalonsAndCitiesSearchOption, SalonsAndCitiesSearchValue } from '../../types/types'
import { SelectProps } from '../../atoms/Select/types'

// styles
import * as SC from './SalonsAndCitiesSearchStyles'
import { BREAKPOINTS_INTS } from '../../styles/constants'

// utils
import { formatDistance, PAGE_LINKS } from '../../utils/helper'
import { AppContext } from '../../utils/appProvider'
import { SALONS_SEARCH_DEBOUNCE_TIME_MS, SEARCH_FIELD_OPTION_TYPE, SELECT_OPTION_CLASS_NAME } from '../../utils/enums'
import { MyLocationContext } from '../../utils/myLocationProvider'
import { getIntl } from '../../utils/intl'

// assets
import MarketIcon from '../../assets/icons/MarketIcon'
import DistancePointIcon from '../../assets/icons/DistancePointIcon'

// components
import Select from '../../atoms/Select/Select'

type ValueTypeForSelectComponent = string | null

type SalonsAndCitiesSearchProps = PropsWithChildren<
	{
		value: SalonsAndCitiesSearchValue | null
		onChange?: (value: SalonsAndCitiesSearchValue) => void
		afterChange?: (value: SalonsAndCitiesSearchValue) => void
		onDropdownVisibleChange?: (open: boolean) => void
		prefixIcon?: React.ReactNode
		suffixIcon?: React.ReactNode
		emptyContent?: {
			emptyIcon?: React.ReactNode
			emptyTitle: React.ReactNode
			emptyLabel?: React.ReactNode
		}
		notFoundContent?: {
			notFoundIcon?: React.ReactNode
			notFoundTitle?: React.ReactNode
			notFoundLabel?: React.ReactNode
		}
		limits?: {
			limitSalons?: GetSalonFiltersQueryParams['limitSalons']
			limitCities?: GetSalonFiltersQueryParams['limitCities']
			limitCategories?: GetSalonFiltersQueryParams['limitCategories']
		}
		showMyLocationOption?: boolean
		redirectQueryData?: SalonPageRedirectQueryData
		isLoadingValue?: boolean
		onFocus?: () => void
		dropdownStyle?: React.CSSProperties
	} & Pick<SelectProps, 'allowClear' | 'placeholder' | 'selectStyle' | 'label' | 'labelScreenReaderOnly'>
>

const SEARCH_FIELD_MY_LOCATION_VALUE = 'SEARCH_FIELD_MY_LOCATION_VALUE'

type MyLocationOptionArgs = { latitude?: number; longitude?: number }

export const GET_MY_LOCATION_OPTION = (args?: MyLocationOptionArgs): SalonsAndCitiesSearchOption => {
	const intl = getIntl()
	const { latitude, longitude } = args || {}

	return {
		key: SEARCH_FIELD_MY_LOCATION_VALUE,
		value: SEARCH_FIELD_MY_LOCATION_VALUE,
		label: intl.formatMessage({ id: 'My location', defaultMessage: 'My location' }),
		className: SELECT_OPTION_CLASS_NAME.BOTTOM_DIVIDER,
		extra: {
			type: SEARCH_FIELD_OPTION_TYPE.CITY,
			latitude,
			longitude
		}
	}
}

const optionRender = (optionProps: SalonsAndCitiesSearchOption) => {
	const isMyLocationOption = optionProps.value === SEARCH_FIELD_MY_LOCATION_VALUE
	return (
		<>
			{optionProps.extra?.type === SEARCH_FIELD_OPTION_TYPE.CITY && (
				<SC.OptionContent>
					<SC.OptionIconWrapper>
						<IconRegularPin width='20' height='20' color={isMyLocationOption ? 'icon.highlight' : 'icon.secondary'} />
					</SC.OptionIconWrapper>
					<SC.CityName
						$myLocationOption={isMyLocationOption}
						dangerouslySetInnerHTML={{
							__html: optionProps.extra.nameSearchHighlighted || optionProps.label
						}}
					/>
				</SC.OptionContent>
			)}
			{optionProps.extra?.type === SEARCH_FIELD_OPTION_TYPE.SALON && (
				<SC.OptionLinkWrapper>
					<SC.SalonOptionContent>
						<SC.OptionIconWrapper>
							<MarketIcon />
						</SC.OptionIconWrapper>
						<SC.SalonContent>
							<SC.SalonName
								dangerouslySetInnerHTML={{
									__html: optionProps.extra.nameSearchHighlighted || optionProps.label
								}}
							/>
							<SC.OptionAddress>
								<SC.Address
									dangerouslySetInnerHTML={{
										__html: optionProps.extra?.addressSearchHighlighted || optionProps.extra?.address || ''
									}}
								/>
								<SC.DistanceWrapper>
									<DistancePointIcon />
									{optionProps.extra?.distance !== undefined && formatDistance(optionProps.extra?.distance)}
								</SC.DistanceWrapper>
							</SC.OptionAddress>
						</SC.SalonContent>
					</SC.SalonOptionContent>
					<SC.ChevronRightIcon />
				</SC.OptionLinkWrapper>
			)}
		</>
	)
}

const getSuffixIconByValue = (value: SalonsAndCitiesSearchValue | null, hasSearchValue: boolean): React.ReactNode => {
	if (!value || hasSearchValue) {
		return <IconRegularSearch />
	}

	if (value.extra?.type === SEARCH_FIELD_OPTION_TYPE.CITY) {
		return <IconRegularPin />
	}

	if (value.extra?.type === SEARCH_FIELD_OPTION_TYPE.SALON) {
		return <MarketIcon />
	}

	return <IconRegularSearch />
}

type BuildDropdownOptionsParams = {
	value: SalonsAndCitiesSearchValue | null
	loadedOptions: SalonsAndCitiesSearchOption[]
	isLoading: boolean
	showMyLocationOption: boolean
	myLocationOption: SalonsAndCitiesSearchOption
	searchQuery: string
}

/**
 * Builds the option list passed to Select: fixed prefix rows (MY_LOCATION) plus debounced API results.
 * Order is always [MY_LOCATION?] [...API rows].
 * The selected value is only kept in the list when the user is not typing, so the closed field can still
 * resolve a display label — pinning while typing caused screen readers to announce "Praha 2 z 2".
 */
const buildSalonsAndCitiesSearchDropdownOptions = (params: BuildDropdownOptionsParams): SalonsAndCitiesSearchOption[] => {
	const { value, loadedOptions, isLoading, showMyLocationOption, myLocationOption, searchQuery } = params
	const hasSearchQuery = Boolean(searchQuery)

	// Keep the current selection only when idle (no search text) so the select can show its label.
	// Never pin while the user is typing — that glued aria-activedescendant to the old selection.
	const shouldPinSelectedValue = value != null && value.value !== myLocationOption.value && !hasSearchQuery

	let prefixOptions: SalonsAndCitiesSearchOption[] = []
	if (shouldPinSelectedValue && value != null) {
		prefixOptions = [{ ...value }]
	}

	// If we kept MY_LOCATION here when the user searched and the API returned zero rows, options would never be [] and
	// Select would not show the “nothing found” empty state. So we hide MY_LOCATION only in that specific case (search text,
	// no rows, no pinned value, request finished). While isLoading is true, MY_LOCATION stays so the list is not empty during spinners handled elsewhere.
	const suppressMyLocationForEmptySearch = hasSearchQuery && loadedOptions.length === 0 && prefixOptions.length === 0 && !isLoading

	if (showMyLocationOption && !suppressMyLocationForEmptySearch) {
		// MY_LOCATION is always the first row when present; pinned value (if any) follows it.
		prefixOptions = [{ ...myLocationOption }, ...prefixOptions]
	}

	// Rows coming from the API must not repeat the selected value, since that copy already lives in prefixOptions when pinned.
	const fromApi = loadedOptions.filter((option) => option.value !== value?.value)
	return [...prefixOptions, ...fromApi]
}

// component
const SalonsAndCitiesSearch = (props: SalonsAndCitiesSearchProps) => {
	const { locale } = useIntl()
	const { messages } = useMessages()

	const {
		onChange,
		value,
		limits,
		allowClear = true,
		placeholder = `${messages['Search for a city or a salon']}...`,
		suffixIcon = <IconRegularSearch />,
		emptyContent,
		notFoundContent,
		showMyLocationOption = false,
		onFocus,
		redirectQueryData,
		selectStyle,
		isLoadingValue,
		dropdownStyle,
		onDropdownVisibleChange,
		prefixIcon,
		label,
		labelScreenReaderOnly
	} = props

	const { limitCategories, limitSalons, limitCities } = limits || {}

	const [loadedOptions, setLoadedOptions] = useState<SalonsAndCitiesSearchOption[]>([])
	const [isLoading, setIsLoading] = useState(false)
	const [searchValue, setSearchValue] = useState('')

	const { myLocation, askForGeolocationPermissions } = useContext(MyLocationContext)

	const isMobile = useIsMobile(BREAKPOINTS_INTS.sm)

	const { apiBrowser } = useContext(AppContext)

	const MY_LOCATION_OPTION = GET_MY_LOCATION_OPTION()

	const resetLoadedOptions = () => {
		setSearchValue('')
		setLoadedOptions([])
	}

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const loadOptionsDebounced = useCallback(
		debounce((inputValue: string) => {
			if (!inputValue) {
				return
			}

			setIsLoading(true)

			apiBrowser.b2c
				.getSalonsFilterData({ search: inputValue, limitSalons, limitCategories, limitCities, latMy: myLocation?.latMy, lonMy: myLocation?.lonMy })
				.then((salonsData) => {
					let newOptions: SalonsAndCitiesSearchOption[] = []

					const citiesOptions: SalonsAndCitiesSearchOption[] = salonsData.cities.map((city) => ({
						key: city.placeID,
						value: city.placeID,
						label: city.name,
						extra: {
							type: SEARCH_FIELD_OPTION_TYPE.CITY,
							longitude: city.longitude,
							latitude: city.latitude,
							nameSearchHighlighted: city.nameSearchHighlighted
						}
					}))
					const salonsOptions: SalonsAndCitiesSearchOption[] = salonsData.salons.map((salon) => ({
						key: salon.id,
						value: salon.id,
						label: salon.name ?? salon.id,
						extra: {
							type: SEARCH_FIELD_OPTION_TYPE.SALON,
							address: `${salon.address?.street}, ${salon.address?.city}`,
							addressSearchHighlighted: salon.address?.addressSearchHighlighted,
							seoSlugName: salon.seoSlugName,
							image: salon.coverImage?.resizedImages.thumbnail,
							distance: salon.distance,
							nameSearchHighlighted: salon.nameSearchHighlighted
						}
					}))

					newOptions = [...salonsOptions, ...citiesOptions]

					setLoadedOptions(newOptions)
				})
				.catch(() => setLoadedOptions([]))
				.finally(() => {
					setIsLoading(false)
				})
		}, SALONS_SEARCH_DEBOUNCE_TIME_MS),
		[myLocation?.latMy, myLocation?.lonMy, limitCategories, limitSalons, limitCities]
	)

	useEffect(() => {
		return () => {
			loadOptionsDebounced.cancel()
			setIsLoading(false)
			setSearchValue('')
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const handleChange: SelectProps<ValueTypeForSelectComponent, SalonsAndCitiesSearchOption>['onChange'] = async (_, option) => {
		let newOption = Array.isArray(option) ? option[0] : option
		setSearchValue('')

		if (newOption) {
			// user choose "my location" value in select
			if (newOption.value === MY_LOCATION_OPTION.value) {
				if (!myLocation) {
					const position = await askForGeolocationPermissions()

					if (!position) {
						return
					}

					newOption = GET_MY_LOCATION_OPTION({ latitude: position.latMy, longitude: position.lonMy })
				}
			}

			if (onChange) {
				onChange(newOption)
			}
		} else {
			resetLoadedOptions()
			if (onChange) {
				onChange(null)
			}
		}

		setIsLoading(false)

		if (newOption?.extra?.type === SEARCH_FIELD_OPTION_TYPE.SALON) {
			// redirect to salon detail page
			onChange?.(newOption)
			const salonDetailPageHref = PAGE_LINKS['/salons/:salonSlug'](locale, newOption.extra.seoSlugName ?? '', redirectQueryData)
			window.location.href = salonDetailPageHref
		}
	}

	// reset loaded options to default
	// because mobile version has slide up animation, we need to have different approaches for both scenarios
	const onDropdownVisibleChangeWrap: SelectProps<ValueTypeForSelectComponent, SalonsAndCitiesSearchOption>['onDropdownVisibleChange'] = (open) => {
		if (onDropdownVisibleChange) {
			onDropdownVisibleChange(open)
		}
		if (open) {
			if (!isMobile) {
				resetLoadedOptions()
			}
		} else if (isMobile) {
			resetLoadedOptions()
		}
	}

	const options = buildSalonsAndCitiesSearchDropdownOptions({
		value,
		loadedOptions,
		isLoading,
		showMyLocationOption,
		myLocationOption: MY_LOCATION_OPTION,
		searchQuery: searchValue
	})

	const getSelectValue = (): ValueTypeForSelectComponent => {
		if (isLoadingValue) {
			return null
		}

		return value?.value ?? null
	}

	return (
		<Select<ValueTypeForSelectComponent, SalonsAndCitiesSearchOption>
			label={label}
			labelScreenReaderOnly={labelScreenReaderOnly}
			selectStyle={selectStyle}
			value={getSelectValue()}
			onChange={handleChange}
			allowClear={allowClear}
			showSearch
			filterOption={false}
			placeholder={isLoadingValue ? messages.Loading : placeholder}
			onSearch={(searchedValue) => {
				setSearchValue(searchedValue)
				setIsLoading(!!searchedValue)
				loadOptionsDebounced(searchedValue)
			}}
			options={options}
			onDropdownVisibleChange={onDropdownVisibleChangeWrap}
			suffixIcon={suffixIcon}
			optionRender={(option) => optionRender(option)}
			prefixIcon={prefixIcon || getSuffixIconByValue(value, Boolean(searchValue))}
			emptyContent={emptyContent}
			notFoundContent={notFoundContent}
			initialLoading={isLoading}
			onClearSearch={resetLoadedOptions}
			disabled={isLoadingValue}
			onFocus={onFocus}
			dropdownStyle={dropdownStyle}
		/>
	)
}

export default SalonsAndCitiesSearch
